/**
 * Human-readable display labels for UserProfile enum fields.
 * Values mirror the backend choices defined in profile_models.py.
 */

export const GENDER_LABELS: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  non_binary: 'Non-binary',
  prefer_not_to_say: 'Prefer not to say',
  other: 'Other',
};

export const GENDER_OPTIONS = Object.entries(GENDER_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const DISABILITY_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  prefer_not_to_say: 'Prefer not to say',
};

export const DISABILITY_OPTIONS = Object.entries(DISABILITY_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const DISABILITY_TYPE_LABELS: Record<string, string> = {
  mobility: 'Mobility',
  visual: 'Visual',
  hearing: 'Hearing',
};

export const DISABILITY_TYPE_OPTIONS = Object.entries(
  DISABILITY_TYPE_LABELS
).map(([value, label]) => ({ value, label }));

export const RACE_LABELS: Record<string, string> = {
  american_indian_alaska_native: 'American Indian or Alaska Native',
  asian: 'Asian',
  black_african_american: 'Black or African American',
  middle_eastern_north_african: 'Middle Eastern or North African',
  multi_racial: 'Multi-Racial',
  native_hawaiian_pacific_islander: 'Native Hawaiian or Other Pacific Islander',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
  white: 'White',
};

export const RACE_OPTIONS = Object.entries(RACE_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const ASIAN_SUB_LABELS: Record<string, string> = {
  asian_indian: 'Asian Indian',
  chinese: 'Chinese',
  filipino: 'Filipino',
  japanese: 'Japanese',
  korean: 'Korean',
  vietnamese: 'Vietnamese',
  other_asian: 'Other Asian',
};

export const ASIAN_SUB_OPTIONS = Object.entries(ASIAN_SUB_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const PACIFIC_ISLANDER_SUB_LABELS: Record<string, string> = {
  native_hawaiian: 'Native Hawaiian',
  guamanian_or_chamorro: 'Guamanian or Chamorro',
  samoan: 'Samoan',
  other_pacific_islander: 'Other Pacific Islander',
};

export const PACIFIC_ISLANDER_SUB_OPTIONS = Object.entries(
  PACIFIC_ISLANDER_SUB_LABELS
).map(([value, label]) => ({ value, label }));

export const ETHNICITY_LABELS: Record<string, string> = {
  hispanic_or_latino: 'Hispanic or Latino',
  not_hispanic_or_latino: 'Not Hispanic or Latino',
  prefer_not_to_say: 'Prefer not to say',
};

export const ETHNICITY_OPTIONS = Object.entries(ETHNICITY_LABELS).map(
  ([value, label]) => ({ value, label })
);

// Keep for backward compat with DataPrivacyPage and other consumers
export const RACE_ETHNICITY_LABELS: Record<string, string> = {
  ...RACE_LABELS,
  hispanic_or_latino: 'Hispanic or Latino',
};

export const CITIZENSHIP_LABELS: Record<string, string> = {
  citizen: 'U.S. Citizen',
  permanent_resident: 'Permanent Resident',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
};

export const CITIZENSHIP_OPTIONS = Object.entries(CITIZENSHIP_LABELS).map(
  ([value, label]) => ({ value, label })
);

// ENG-3602: strict Yes/No for the mobility / accommodation demographics
// (UserProfile.mobility_disability, .mobility_disability_expected_12mo,
// .special_accommodation_needed). Mirrors the backend YesNo choices — these are
// booleans backed by hidden questionnaire QITs, not the tri-state disability set.
export const YES_NO_LABELS: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
};

export const YES_NO_OPTIONS = Object.entries(YES_NO_LABELS).map(
  ([value, label]) => ({ value, label })
);

export function raceEthnicityDisplay(values: string[]): string {
  if (!values || values.length === 0) {
    return '—';
  }
  return values.map((v) => RACE_ETHNICITY_LABELS[v] ?? v).join(', ');
}
